import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Formik, getIn } from 'formik';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import config from '../../../config';
import { getAuthToken } from '../../../utils/authStorage';
import HpcShell from '../HpcShell';
import { HPC_COLORS } from '../hpcTheme';
import {
  ASSESSMENT_STEPS,
  CARDIO_OPTIONS,
  CORRECTIVE_PRIORITIES,
  FITNESS_FIELD_HINTS,
  FITNESS_INPUT_FIELDS,
  FMS_TESTS,
  HEALTH_FIELD_HINTS,
  HEALTH_NUMERIC_FIELDS,
  MOBILITY_TESTS,
  POSTURE_FIELDS,
  POSTURE_NOTES_HINT,
  SCORE_LABELS,
  SUMMARY_FIELD_HINTS,
  assessmentFormValues,
  formatMobilityClass,
  formatRiskLabel,
} from './assessmentFormConfig';
import HealthCalculators from './HealthCalculators';
import {
  calculateFmsRisk,
  calculateMobilityClass,
  generateSummaryPreview,
  sumScores,
} from './assessmentCalculations';
import { validateAssessmentStep } from './assessmentValidation';
import { getAssessmentStatusChipSx } from './assessmentStatusStyles';
import { downloadAssessmentPdf } from './assessmentExport';

const authHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const sectionFieldError = (errors, showStepErrors, section, key) => {
  if (!showStepErrors) return undefined;
  return getIn(errors, `${section}.${key}`);
};

const applyValidationErrors = (validationResult, setErrors, setFieldTouched) => {
  const { errorMap, items } = validationResult;
  setErrors(errorMap);
  items.forEach((err) => {
    if (err.path) setFieldTouched(err.path, true, false);
  });
};

const ERROR_COLOR = '#f44336';

const assessmentRadioSx = (error) => ({
  color: error ? 'rgba(244, 67, 54, 0.5)' : 'rgba(184, 197, 214, 0.45)',
  '&.Mui-checked': {
    color: error ? ERROR_COLOR : HPC_COLORS.hpuOrange,
  },
  '&:hover': {
    color: error ? ERROR_COLOR : HPC_COLORS.hpuOrange,
  },
});

const assessmentChoiceSx = (selected, error) => ({
  mx: 0,
  px: 1.25,
  py: 0.35,
  borderRadius: 2,
  border: '2px solid',
  borderColor: selected
    ? (error ? ERROR_COLOR : HPC_COLORS.hpuOrange)
    : 'rgba(184, 197, 214, 0.35)',
  bgcolor: selected
    ? (error ? 'rgba(244, 67, 54, 0.12)' : 'rgba(245, 166, 35, 0.22)')
    : 'rgba(0, 0, 0, 0.15)',
  transition: 'border-color 0.15s, background-color 0.15s',
  '& .MuiFormControlLabel-label': {
    color: selected ? HPC_COLORS.white : HPC_COLORS.silver,
    fontWeight: selected ? 700 : 400,
  },
  '&:hover': {
    borderColor: error ? ERROR_COLOR : HPC_COLORS.hpuOrange,
    bgcolor: selected
      ? (error ? 'rgba(244, 67, 54, 0.12)' : 'rgba(245, 166, 35, 0.22)')
      : 'rgba(245, 166, 35, 0.08)',
  },
});

const assessmentCheckboxSx = (error) => ({
  color: error ? 'rgba(244, 67, 54, 0.5)' : 'rgba(184, 197, 214, 0.45)',
  p: 0.5,
  '&.Mui-checked': {
    color: error ? ERROR_COLOR : HPC_COLORS.hpuOrange,
  },
  '&:hover': {
    color: error ? ERROR_COLOR : HPC_COLORS.hpuOrange,
  },
  '&.Mui-disabled': {
    color: 'rgba(184, 197, 214, 0.35)',
    '&.Mui-checked': {
      color: HPC_COLORS.hpuOrange,
    },
  },
});

const assessmentSectionLabelSx = (error) => ({
  color: error ? ERROR_COLOR : HPC_COLORS.white,
  fontWeight: 600,
  mb: 1,
  display: 'block',
  padding: 0,
  float: 'none',
  width: 'auto',
});

const AssessmentChoiceGroup = ({ value, onChange, options, row = false, error }) => (
  <RadioGroup
    row={row}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    sx={{
      flexDirection: row ? 'row' : 'column',
      flexWrap: 'wrap',
      gap: 1,
      alignItems: row ? 'center' : 'flex-start',
    }}
  >
    {options.map((opt) => {
      const optValue = String(opt.value);
      const selected = String(value) === optValue;
      return (
        <FormControlLabel
          key={optValue}
          value={optValue}
          control={<Radio size="small" sx={assessmentRadioSx(error)} />}
          label={opt.label}
          sx={assessmentChoiceSx(selected, error)}
        />
      );
    })}
  </RadioGroup>
);

const fieldSx = {
  '& .MuiInputLabel-root': { color: HPC_COLORS.silver },
  '& .MuiInputLabel-root.Mui-error': { color: ERROR_COLOR },
  '& .MuiFormHelperText-root': { color: 'rgba(192, 192, 192, 0.85)', mt: 0.5 },
  '& .MuiFormHelperText-root.Mui-error': { color: ERROR_COLOR },
  '& .MuiOutlinedInput-root': {
    color: HPC_COLORS.white,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
    '&.Mui-focused fieldset': { borderColor: HPC_COLORS.hpuOrange },
    '&.Mui-error fieldset': { borderColor: ERROR_COLOR },
    '&.Mui-error:hover fieldset': { borderColor: ERROR_COLOR },
    '&.Mui-error.Mui-focused fieldset': { borderColor: ERROR_COLOR },
  },
};

const ScoreRadioGroup = ({ value, onChange, label, error }) => (
  <Box sx={{ mb: 2 }}>
    <Typography component="div" sx={assessmentSectionLabelSx(error)}>
      {label}
    </Typography>
    <AssessmentChoiceGroup
      row
      value={value}
      onChange={onChange}
      options={SCORE_LABELS.map((opt) => ({
        value: String(opt.value),
        label: opt.label,
      }))}
      error={error}
    />
    {error && <FormHelperText error sx={{ mt: 0.5 }}>{error}</FormHelperText>}
  </Box>
);

const MemberStep = ({ snapshot, assessmentDate, status }) => (
  <Box>
    <Box display="flex" gap={1} alignItems="center" mb={2}>
      <Typography variant="h6" sx={{ color: HPC_COLORS.white, fontWeight: 700 }}>
        Member Information
      </Typography>
      <Chip label={status} size="small" sx={getAssessmentStatusChipSx(status)} />
    </Box>
    <Typography sx={{ color: HPC_COLORS.silver, mb: 2 }}>
      Assessment date: <strong style={{ color: HPC_COLORS.hpuOrange }}>{assessmentDate}</strong>
    </Typography>
    <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mb: 2 }} />
    {[
      ['Full Name', snapshot?.full_name],
      ['Membership ID', snapshot?.membership_id],
      ['Age', snapshot?.age],
      ['Gender', snapshot?.gender],
      ['Mobile', snapshot?.mobile_number],
      ['Email', snapshot?.email],
      ['Emergency Contact', snapshot?.emergency_contact_person],
      ['Emergency Number', snapshot?.emergency_contact_number],
    ].map(([label, val]) => (
      <Box key={label} display="flex" justifyContent="space-between" py={0.75}>
        <Typography sx={{ color: HPC_COLORS.silver }}>{label}</Typography>
        <Typography sx={{ color: HPC_COLORS.white, fontWeight: 600 }}>{val || '—'}</Typography>
      </Box>
    ))}
  </Box>
);

const HealthStep = ({ values, setFieldValue, errors, showStepErrors }) => {
  const health = values.health_screening;

  const getFieldError = (key) =>
    sectionFieldError(errors, showStepErrors, 'health_screening', key);

  const hasStepErrors = showStepErrors
    && Object.values(getIn(errors, 'health_screening') || {}).some(Boolean);

  return (
    <Box>
      {hasStepErrors && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Please complete the required fields highlighted below.
        </Alert>
      )}
      <Typography variant="h6" sx={{ color: HPC_COLORS.white, fontWeight: 700, mb: 2 }}>
        Health Screening
      </Typography>
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
        {HEALTH_NUMERIC_FIELDS.map(({ key, label, type, required }) => {
          const hint = HEALTH_FIELD_HINTS[key] || {};
          const error = getFieldError(key);
          return (
            <TextField
              key={key}
              fullWidth
              required={Boolean(required)}
              label={label}
              type={type || 'text'}
              placeholder={hint.placeholder}
              helperText={error || hint.helperText}
              error={Boolean(error)}
              value={health[key]}
              onChange={(e) => setFieldValue(`health_screening.${key}`, e.target.value)}
              sx={fieldSx}
            />
          );
        })}
      </Box>
      <TextField
        fullWidth multiline minRows={2} label="Medical Conditions" sx={{ ...fieldSx, mt: 2 }}
        placeholder={HEALTH_FIELD_HINTS.medical_conditions.placeholder}
        helperText={HEALTH_FIELD_HINTS.medical_conditions.helperText}
        value={health.medical_conditions}
        onChange={(e) => setFieldValue('health_screening.medical_conditions', e.target.value)}
      />
      <TextField
        fullWidth multiline minRows={2} label="Medications" sx={{ ...fieldSx, mt: 2 }}
        placeholder={HEALTH_FIELD_HINTS.medications.placeholder}
        helperText={HEALTH_FIELD_HINTS.medications.helperText}
        value={health.medications}
        onChange={(e) => setFieldValue('health_screening.medications', e.target.value)}
      />
      <TextField
        fullWidth multiline minRows={2} label="Exercise Experience" sx={{ ...fieldSx, mt: 2 }}
        placeholder={HEALTH_FIELD_HINTS.exercise_experience.placeholder}
        helperText={HEALTH_FIELD_HINTS.exercise_experience.helperText}
        value={health.exercise_experience}
        onChange={(e) => setFieldValue('health_screening.exercise_experience', e.target.value)}
      />
      <TextField
        fullWidth label="Emergency Contact" sx={{ ...fieldSx, mt: 2 }}
        placeholder={HEALTH_FIELD_HINTS.emergency_contact.placeholder}
        helperText={HEALTH_FIELD_HINTS.emergency_contact.helperText}
        value={health.emergency_contact}
        onChange={(e) => setFieldValue('health_screening.emergency_contact', e.target.value)}
      />
      <TextField
        fullWidth multiline minRows={2} label="Notes" sx={{ ...fieldSx, mt: 2 }}
        placeholder={HEALTH_FIELD_HINTS.notes.placeholder}
        helperText={HEALTH_FIELD_HINTS.notes.helperText}
        value={health.notes}
        onChange={(e) => setFieldValue('health_screening.notes', e.target.value)}
      />

      <HealthCalculators health={health} />

      <TextField
        fullWidth multiline minRows={2} label="Clinical Notes (optional)" sx={{ ...fieldSx, mt: 2 }}
        placeholder={HEALTH_FIELD_HINTS.risk_override_comment.placeholder}
        helperText={HEALTH_FIELD_HINTS.risk_override_comment.helperText}
        value={health.risk_override_comment}
        onChange={(e) => setFieldValue('health_screening.risk_override_comment', e.target.value)}
      />
    </Box>
  );
};

const PostureStep = ({ values, setFieldValue, errors, touched, showStepErrors }) => (
  <Box>
    <Typography variant="h6" sx={{ color: HPC_COLORS.white, fontWeight: 700, mb: 2 }}>
      Posture Assessment
    </Typography>
    {POSTURE_FIELDS.map((field) => {
      const error = sectionFieldError(errors, showStepErrors, 'posture', field.key);
      return (
      <FormControl key={field.key} component="fieldset" fullWidth sx={{ mb: 2.5, border: 0, p: 0, m: 0, minWidth: 0 }} error={Boolean(error)}>
        <Typography component="legend" sx={assessmentSectionLabelSx(error)}>
          {field.label}
        </Typography>
        <AssessmentChoiceGroup
          value={values.posture[field.key]}
          onChange={(v) => setFieldValue(`posture.${field.key}`, v)}
          options={field.options}
          row
          error={error}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
      );
    })}
    <TextField
      fullWidth multiline minRows={3} label="Notes" sx={fieldSx}
      placeholder={POSTURE_NOTES_HINT.placeholder}
      helperText={POSTURE_NOTES_HINT.helperText}
      value={values.posture.notes}
      onChange={(e) => setFieldValue('posture.notes', e.target.value)}
    />
  </Box>
);

const FmsStep = ({ values, setFieldValue, errors, touched, showStepErrors }) => {
  const total = sumScores(values.functional_movement, FMS_TESTS.map((t) => t.key));
  const risk = calculateFmsRisk(total);
  return (
    <Box>
      <Typography variant="h6" sx={{ color: HPC_COLORS.white, fontWeight: 700, mb: 1 }}>
        Functional Movement Screening
      </Typography>
      <Typography sx={{ color: HPC_COLORS.silver, mb: 1 }}>
        Total: <strong style={{ color: HPC_COLORS.hpuOrange }}>{total ?? '—'}</strong> / 21 · Risk:{' '}
        <strong style={{ color: HPC_COLORS.hpuOrange }}>{formatRiskLabel(risk)}</strong>
      </Typography>
      <Typography sx={{ color: HPC_COLORS.silver, mb: 2, fontSize: '0.85rem' }}>
        Score each test: 0 = Pain · 1 = Limited · 2 = Acceptable · 3 = Excellent
      </Typography>
      {FMS_TESTS.map((test) => (
        <ScoreRadioGroup
          key={test.key}
          label={test.label}
          value={values.functional_movement[test.key]}
          onChange={(v) => setFieldValue(`functional_movement.${test.key}`, v)}
          error={sectionFieldError(errors, showStepErrors, 'functional_movement', test.key)}
        />
      ))}
    </Box>
  );
};

const MobilityStep = ({ values, setFieldValue, errors, touched, showStepErrors }) => {
  const total = sumScores(values.mobility, MOBILITY_TESTS.map((t) => t.key));
  const classification = calculateMobilityClass(total);
  return (
    <Box>
      <Typography variant="h6" sx={{ color: HPC_COLORS.white, fontWeight: 700, mb: 1 }}>
        Mobility Assessment
      </Typography>
      <Typography sx={{ color: HPC_COLORS.silver, mb: 1 }}>
        Total: <strong style={{ color: HPC_COLORS.hpuOrange }}>{total ?? '—'}</strong> / 24 ·{' '}
        <strong style={{ color: HPC_COLORS.hpuOrange }}>{formatMobilityClass(classification)}</strong>
      </Typography>
      <Typography sx={{ color: HPC_COLORS.silver, mb: 2, fontSize: '0.85rem' }}>
        Score each test: 0 = Pain · 1 = Limited · 2 = Acceptable · 3 = Excellent
      </Typography>
      {MOBILITY_TESTS.map((test) => (
        <ScoreRadioGroup
          key={test.key}
          label={test.label}
          value={values.mobility[test.key]}
          onChange={(v) => setFieldValue(`mobility.${test.key}`, v)}
          error={sectionFieldError(errors, showStepErrors, 'mobility', test.key)}
        />
      ))}
    </Box>
  );
};

const FitnessStep = ({ values, setFieldValue, history }) => {
  const cardioMethod = values.fitness_testing.cardio_method;
  const cardioHint =
    cardioMethod === 'three_min_step'
      ? FITNESS_FIELD_HINTS.cardio_result_step
      : cardioMethod === 'six_min_walk'
        ? FITNESS_FIELD_HINTS.cardio_result_walk
        : FITNESS_FIELD_HINTS.cardio_result;

  return (
  <Box>
    <Typography variant="h6" sx={{ color: HPC_COLORS.white, fontWeight: 700, mb: 2 }}>
      Fitness Testing
    </Typography>
    <TextField
      select fullWidth label="Cardiovascular Test" sx={{ ...fieldSx, mb: 2 }}
      value={values.fitness_testing.cardio_method}
      onChange={(e) => setFieldValue('fitness_testing.cardio_method', e.target.value)}
    >
      <MenuItem value="">None</MenuItem>
      {CARDIO_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </TextField>
    <TextField
      fullWidth type="number" label="Cardio Result" sx={{ ...fieldSx, mb: 2 }}
      placeholder={cardioHint.placeholder}
      helperText={cardioHint.helperText}
      value={values.fitness_testing.cardio_result}
      onChange={(e) => setFieldValue('fitness_testing.cardio_result', e.target.value)}
    />
    <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
      {FITNESS_INPUT_FIELDS.map(({ key, label }) => {
        const hint = FITNESS_FIELD_HINTS[key] || {};
        return (
          <TextField
            key={key}
            fullWidth
            type="number"
            label={label}
            placeholder={hint.placeholder}
            helperText={hint.helperText}
            value={values.fitness_testing[key]}
            onChange={(e) => setFieldValue(`fitness_testing.${key}`, e.target.value)}
            sx={fieldSx}
          />
        );
      })}
    </Box>

    {history.length > 0 && (
      <Box mt={3}>
        <Typography sx={{ color: HPC_COLORS.white, fontWeight: 700, mb: 1 }}>
          Prior Fitness Results
        </Typography>
        <Table size="small" sx={{ '& td, & th': { color: HPC_COLORS.silver, borderColor: 'rgba(255,255,255,0.12)' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Push-ups</TableCell>
              <TableCell>Plank</TableCell>
              <TableCell>30s S-T-S</TableCell>
              <TableCell>BMI</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.assessment_date}</TableCell>
                <TableCell>{row.fitness_testing?.push_ups ?? '—'}</TableCell>
                <TableCell>{row.fitness_testing?.plank_seconds ?? '—'}</TableCell>
                <TableCell>{row.fitness_testing?.sit_to_stand_30s ?? '—'}</TableCell>
                <TableCell>{row.bmi ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )}
  </Box>
  );
};

const SummaryStep = ({ values, setFieldValue, readOnly }) => (
  <Box>
    <Typography variant="h6" sx={{ color: HPC_COLORS.white, fontWeight: 700, mb: 2 }}>
      Assessment Summary
    </Typography>
    <TextField
      fullWidth multiline minRows={4} label="Strengths" sx={{ ...fieldSx, mb: 2 }}
      placeholder={SUMMARY_FIELD_HINTS.strengths.placeholder}
      helperText={SUMMARY_FIELD_HINTS.strengths.helperText}
      value={values.summary.strengths}
      onChange={(e) => setFieldValue('summary.strengths', e.target.value)}
      InputProps={{ readOnly }}
    />
    <TextField
      fullWidth multiline minRows={4} label="Areas for Improvement" sx={{ ...fieldSx, mb: 2 }}
      placeholder={SUMMARY_FIELD_HINTS.areas_for_improvement.placeholder}
      helperText={SUMMARY_FIELD_HINTS.areas_for_improvement.helperText}
      value={values.summary.areas_for_improvement}
      onChange={(e) => setFieldValue('summary.areas_for_improvement', e.target.value)}
      InputProps={{ readOnly }}
    />
    <Typography component="div" sx={assessmentSectionLabelSx(false)}>Corrective Priorities</Typography>
    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
      {CORRECTIVE_PRIORITIES.map((item) => {
        const checked = values.summary.corrective_priorities.includes(item.value);
        return (
          <FormControlLabel
            key={item.value}
            sx={assessmentChoiceSx(checked, false)}
            control={
              <Checkbox
                checked={checked}
                disabled={readOnly}
                size="small"
                onChange={(e) => {
                  const current = values.summary.corrective_priorities;
                  const next = e.target.checked
                    ? [...current, item.value]
                    : current.filter((v) => v !== item.value);
                  setFieldValue('summary.corrective_priorities', next);
                }}
                sx={assessmentCheckboxSx(false)}
              />
            }
            label={item.label}
          />
        );
      })}
    </Box>
    <TextField
      fullWidth multiline minRows={3} label="Trainer Notes" sx={fieldSx}
      placeholder={SUMMARY_FIELD_HINTS.trainer_notes.placeholder}
      helperText={SUMMARY_FIELD_HINTS.trainer_notes.helperText}
      value={values.summary.trainer_notes}
      onChange={(e) => setFieldValue('summary.trainer_notes', e.target.value)}
      InputProps={{ readOnly }}
    />
  </Box>
);

const SECTION_KEYS = [
  null,
  'health_screening',
  'posture',
  'functional_movement',
  'mobility',
  'fitness_testing',
  'summary',
];

const AssessmentWizard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [showStepErrors, setShowStepErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const readOnly = assessment?.status === 'reviewed';

  const loadAssessment = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.apiBaseUrl}/hpc/admin/assessments/${id}`, {
        headers: authHeaders(),
      });
      const data = response.data.assessment;
      setAssessment(data);

      const histRes = await axios.get(
        `${config.apiBaseUrl}/hpc/admin/members/${data.membership_id}/assessment-history`,
        { params: { exclude_id: id }, headers: authHeaders() }
      );
      setHistory(histRes.data.history || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load assessment');
      navigate('/hpc/assessments');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadAssessment();
  }, [loadAssessment]);

  useEffect(() => {
    setShowStepErrors(false);
  }, [activeStep]);

  const initialValues = useMemo(
    () => (assessment ? assessmentFormValues(assessment) : null),
    [assessment]
  );

  const saveSection = async (values, step) => {
    const sectionKey = SECTION_KEYS[step];
    if (!sectionKey) return true;

    setSaving(true);
    try {
      const payload = { [sectionKey]: values[sectionKey] };
      const response = await axios.patch(
        `${config.apiBaseUrl}/hpc/admin/assessments/${id}`,
        payload,
        { headers: authHeaders() }
      );
      setAssessment(response.data.assessment);
      toast.success('Draft saved');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (values, setFieldValue, setErrors, setFieldTouched, setActiveStepFn) => {
    for (let step = 1; step < ASSESSMENT_STEPS.length; step += 1) {
      const validationResult = await validateAssessmentStep(step, values);
      if (validationResult.items.length > 0) {
        setActiveStepFn(step);
        applyValidationErrors(validationResult, setErrors, setFieldTouched);
        setShowStepErrors(true);
        toast.error('Please complete required fields before completing');
        return;
      }
    }

    const summary = values.summary?.strengths
      ? values.summary
      : generateSummaryPreview(values);
    if (!values.summary?.strengths) {
      setFieldValue('summary', summary);
    }
    await saveSection({ ...values, summary }, 6);
    setSaving(true);
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/hpc/admin/assessments/${id}/complete`,
        { summary: values.summary?.strengths ? values.summary : generateSummaryPreview(values) },
        { headers: authHeaders() }
      );
      setAssessment(response.data.assessment);
      toast.success('Assessment completed');
    } catch (error) {
      const details = error.response?.data?.details;
      if (details?.length) {
        toast.error(details.join('; '));
      } else {
        toast.error(error.response?.data?.error || 'Could not complete assessment');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async (values) => {
    setExporting(true);
    try {
      if (!readOnly && activeStep > 0) {
        const saved = await saveSection(values, activeStep);
        if (!saved) return;
      }
      await downloadAssessmentPdf(id, {
        membershipId: assessment.membership_id,
        assessmentDate: assessment.assessment_date,
      });
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading || !initialValues) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <ToastContainer position="top-center" theme="dark" />
      <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={() => {}}
    >
      {({ values, setFieldValue, setFieldTouched, setErrors, errors, touched }) => {
        const stepRenderers = [
          () => (
            <MemberStep
              snapshot={assessment.member_snapshot}
              assessmentDate={assessment.assessment_date}
              status={assessment.status}
            />
          ),
          () => (
            <HealthStep
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              showStepErrors={showStepErrors}
            />
          ),
          () => (
            <PostureStep
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              showStepErrors={showStepErrors}
            />
          ),
          () => (
            <FmsStep
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              showStepErrors={showStepErrors}
            />
          ),
          () => (
            <MobilityStep
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              showStepErrors={showStepErrors}
            />
          ),
          () => <FitnessStep values={values} setFieldValue={setFieldValue} history={history} />,
          () => <SummaryStep values={values} setFieldValue={setFieldValue} readOnly={readOnly} />,
        ];

        const handleContinue = async () => {
          if (readOnly) {
            setActiveStep((s) => Math.min(s + 1, ASSESSMENT_STEPS.length - 1));
            return;
          }
          const validationResult = await validateAssessmentStep(activeStep, values);
          if (validationResult.items.length > 0) {
            applyValidationErrors(validationResult, setErrors, setFieldTouched);
            setShowStepErrors(true);
            toast.error('Please complete required fields on this step');
            return;
          }
          setShowStepErrors(false);
          if (activeStep > 0) {
            const saved = await saveSection(values, activeStep);
            if (!saved) return;
          }
          const nextStep = Math.min(activeStep + 1, ASSESSMENT_STEPS.length - 1);
          if (nextStep === 6 && !readOnly) {
            const preview = generateSummaryPreview(values);
            setFieldValue('summary', preview);
          }
          setActiveStep(nextStep);
        };

        const handleSaveDraft = async () => {
          const validationResult = await validateAssessmentStep(activeStep, values);
          if (validationResult.items.length > 0) {
            applyValidationErrors(validationResult, setErrors, setFieldTouched);
            setShowStepErrors(true);
            toast.error('Please complete required fields on this step');
            return;
          }
          setShowStepErrors(false);
          await saveSection(values, activeStep);
        };

        const footer = (
          <>
            <Button
              variant="outlined"
              disabled={activeStep === 0 || saving}
              onClick={() => setActiveStep((s) => s - 1)}
              sx={{ flex: 1, color: HPC_COLORS.white, borderColor: HPC_COLORS.silver }}
            >
              Back
            </Button>
            {!readOnly && activeStep > 0 && activeStep < ASSESSMENT_STEPS.length - 1 && (
              <Button
                variant="outlined"
                disabled={saving}
                onClick={handleSaveDraft}
                sx={{ flex: 1, color: HPC_COLORS.hpuOrange, borderColor: HPC_COLORS.hpuOrange }}
              >
                Save Draft
              </Button>
            )}
            {activeStep < ASSESSMENT_STEPS.length - 1 ? (
              <Button
                variant="contained"
                disabled={saving}
                onClick={handleContinue}
                sx={{ flex: 1, bgcolor: HPC_COLORS.hpuOrange, color: '#1a1200', fontWeight: 700 }}
              >
                Continue
              </Button>
            ) : (
              <>
                {!readOnly && assessment.status !== 'reviewed' && (
                  <Button
                    variant="contained"
                    disabled={saving}
                    onClick={() => handleComplete(values, setFieldValue, setErrors, setFieldTouched, setActiveStep)}
                    sx={{ flex: 1, bgcolor: HPC_COLORS.hpuOrange, color: '#1a1200', fontWeight: 700 }}
                  >
                    {assessment.status === 'completed' ? 'Update & Complete' : 'Complete'}
                  </Button>
                )}
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              disabled={saving || exporting}
              onClick={() => handleExportPdf(values)}
              sx={{
                flex: 1,
                color: HPC_COLORS.white,
                borderColor: HPC_COLORS.tebaBlue,
                fontWeight: 600,
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="text"
              onClick={() => navigate('/hpc/assessments')}
              sx={{ color: HPC_COLORS.silver }}
            >
              Exit
            </Button>
          </>
        );

        return (
          <HpcShell
            activeStep={activeStep}
            steps={ASSESSMENT_STEPS}
            wizardTitle="Initial Screening & Assessment"
            footer={footer}
          >
            {assessment.status === 'reviewed' && activeStep < ASSESSMENT_STEPS.length - 1 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This assessment has been reviewed and cannot be edited.
              </Alert>
            )}
            {stepRenderers[activeStep]()}
          </HpcShell>
        );
      }}
    </Formik>
    </>
  );
};

export default AssessmentWizard;
